import {
  ENTITY_TYPE_MAP,
  VERIFICATION_TYPES,
} from '@fantaz/shared/config/static';
import {
  Address,
  CreditCard,
  Location,
  ProfileMatch,
} from '@fantaz/shared/data-access/models';

import { VerifyUserExceptions } from './account-service.failures';
import {
  BusinessGameProfile,
  IVerifyUser,
  IVerifyUserWithCreditCard,
  UserGameProfile,
  VerificationAttemptLimits,
} from './account-service.interface';

export class VerifyUserDto implements IVerifyUser {
  businessName?: string;
  stateOfFormation?: string;
  classification?: {
    name: string;
    type: string;
    otherType?: string;
  };
  other?: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  address: Address;
  entity?: boolean;
  ssn?: string;
  ein?: string;
  stateRegionDiff?: boolean;
  stateRegionForTax?: string;
  accurateCheckBox?: boolean;
  ageLocationCheckBox?: boolean;
  legalRepCheckBox?: boolean;
  userUuid?: string;
  lastFour?: string;
  differentStateForTaxPurposes?: string;
  stateForTaxPurposes?: string;
  correctInformation?: boolean;
  legalAge?: boolean;
  legalRepresentative: boolean;

  constructor(init?: Partial<VerifyUserDto>) {
    Object.assign(this, init);
  }

  static fromJson(obj: Partial<VerifyUserDto>) {
    return new VerifyUserDto(obj);
  }

  toJson() {
    return {
      firstName: this.firstName,
      lastName: this.lastName,
      address1: this.address.addressLine1,
      city: this.address.city,
      stateRegion: this.address.state,
      zip: this.address.zipCode,
      dob: this.dateOfBirth,
      email: this.email,
      entity: this.entity,
      userUuid: this.userUuid,
      businessName: this.businessName,
      stateOfFormation: this.stateOfFormation,
      ein: this.ein,
      taxClassification: this.classification,
      legalRepresentative: this.legalRepresentative,
      address2: this.address.addressLine2,
      last4: this.lastFour,
      stateRegionForTax: this.stateRegionForTax,
      countryCode: this.address.country,
      ...(this.ssn && { ssn: this.ssn }),
    };
  }
}

export class VerifyUserResponseDto {
  name?: string;
  status: keyof typeof VERIFICATION_TYPES | string;
  profileMatch?: ProfileMatch[];
  attempts?: VerificationAttemptLimits;

  constructor(init?: Partial<VerifyUserResponseDto>) {
    Object.assign(this, init);
  }

  static fromJson(obj: Partial<VerifyUserResponseDto>) {
    // DUPLICATE is a priority status
    if (obj.status === VERIFICATION_TYPES.DUPLICATE) {
      throw VerifyUserExceptions.duplicated(obj.profileMatch || []);
    }

    // Verify if the user has reached the maximum number of attempts
    if (obj.attempts) {
      let creditCardRemainAttempts = 0;
      let customerIdentityRemainAttempts = 0;

      if (obj.attempts.current.CC >= 0 && obj.attempts.max.CC >= 0) {
        creditCardRemainAttempts =
          obj.attempts.max.CC - obj.attempts.current.CC;
      }

      if (
        obj.attempts.current.CUSTOMER_IDENTITY >= 0 &&
        obj.attempts.max.CUSTOMER_IDENTITY >= 0
      ) {
        customerIdentityRemainAttempts =
          obj.attempts.max.CUSTOMER_IDENTITY -
          obj.attempts.current.CUSTOMER_IDENTITY;
      }

      if (
        obj.attempts.current.DOCUMENT >= 0 &&
        obj.attempts.max.DOCUMENT >= 0
      ) {
        customerIdentityRemainAttempts =
          obj.attempts.max.DOCUMENT - obj.attempts.current.DOCUMENT;
      }

      if (creditCardRemainAttempts > 0 || customerIdentityRemainAttempts > 0) {
        throw VerifyUserExceptions.incomplete(obj);
      }
    }

    // TODO: Fix BE with code for verification status {type}_VERIFICATION_ATTEMPTS_EXCEEDED

    switch (obj.status) {
      case VERIFICATION_TYPES.ESCALATE:
      case VERIFICATION_TYPES.IDENTITY_VERIFICATION_ATTEMPTS_EXCEEDED: // for CUSTOMER_IDENTITY_VERIFICATION_ATTEMPTS_EXCEEDED
        throw VerifyUserExceptions.escalate();
      case VERIFICATION_TYPES.NONE:
        throw VerifyUserExceptions.none();
      case VERIFICATION_TYPES.REJECTED:
        throw VerifyUserExceptions.rejected();
      case VERIFICATION_TYPES.INCOMPLETE:
        // TODO: Case for incomplete
        // case VERIFICATION_TYPES.CC_VERIFICATION_ATTEMPTS_EXCEEDED:
        // case VERIFICATION_TYPES.DOCUMENT_VERIFICATION_ATTEMPTS_EXCEEDED:
        // case VERIFICATION_TYPES.CUSTOMER_IDENTITY_VERIFICATION_ATTEMPTS_EXCEEDED:
        throw VerifyUserExceptions.incomplete(obj);
    }

    return new VerifyUserResponseDto(obj);
  }
}

export class VerifyUserWithCreditCardDto implements IVerifyUserWithCreditCard {
  user: { uuid: string };
  card: CreditCard;
  userLocation: {
    deviceGPS?: Location | null;
    userAllowToUseFallbackLocation?: boolean | null;
  };

  constructor(data?: Partial<VerifyUserWithCreditCardDto>) {
    Object.assign(this, data);
  }
}

export class UserGameProfileDto implements UserGameProfile {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  address: Address;
  phone?: string | undefined;
  ssn?: string | undefined;
  userUuid?: string | undefined;
  lastFour?: string | undefined;

  constructor(data?: Partial<UserGameProfileDto>) {
    // TODO: Validate required data
    Object.assign(this, data);
  }

  toJson() {
    return {
      firstName: this.firstName,
      lastName: this.lastName,
      birthday: this.dateOfBirth,
      phone: this.phone,
      legalAddress: this.address,
      shippingAddress: this.address,
      entity: ENTITY_TYPE_MAP.INDIVIDUAL,
    };
  }
}

export class BusinessGameProfileDto implements BusinessGameProfile {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  address: Address;
  phone?: string | undefined;
  ssn?: string | undefined;
  userUuid?: string | undefined;
  lastFour?: string | undefined;

  businessName?: string;
  stateOfFormation?: string;
  classification?: {
    name: string;
    type: string;
    otherType?: string;
  };
  other?: string;
  ein?: string;
  stateRegionDiff?: boolean;
  stateRegionForTax?: string;
  accurateCheckBox?: boolean;
  ageLocationCheckBox?: boolean;
  legalRepCheckBox?: boolean;
  differentStateForTaxPurposes?: string;
  stateForTaxPurposes?: string;
  correctInformation?: boolean;
  legalAge?: boolean;
  legalRepresentative: boolean;

  constructor(data?: Partial<UserGameProfileDto>) {
    // TODO: Validate required data
    Object.assign(this, data);
  }

  toJson() {
    return {
      firstName: this.firstName,
      lastName: this.lastName,
      birthday: this.dateOfBirth,
      phone: this.phone,
      legalAddress: this.address,
      shippingAddress: this.address,
      address: this.address,
      entity: ENTITY_TYPE_MAP.BUSINESS,
      businessName: this.businessName,
      stateOfFormation: this.stateOfFormation,
      classification: this.classification,
      other: this.other,
      ein: this.ein,
      stateRegionDiff: this.stateRegionDiff,
      stateRegionForTax: this.stateRegionForTax,
      accurateCheckBox: this.accurateCheckBox,
      ageLocationCheckBox: this.ageLocationCheckBox,
      legalRepCheckBox: this.legalRepCheckBox,
      differentStateForTaxPurposes: this.differentStateForTaxPurposes,
      stateForTaxPurposes: this.stateForTaxPurposes,
      correctInformation: this.correctInformation,
      legalAge: this.legalAge,
      legalRepresentative: this.legalRepresentative,
    };
  }
}
